/**
 * @fileoverview useBudgetTemplate - Budget template management hook.
 *
 * Manages the budget template which defines spending limits and income allocation
 * percentages for each category. The template is used to guide monthly budgeting.
 *
 * @description This hook provides:
 * - Budget template loading from backend
 * - Real-time template updates with debounced saves
 * - Category addition/removal from template
 * - Validation helpers (percentage totals, max budgets)
 * - Automatic budget context subscription
 *
 * Budget template structure:
 * {
 *   "Groceries": { target: 1000, percentage: 0 },
 *   "Entertainment": { target: 500, percentage: 15 },
 *   ...
 * }
 *
 * Key features:
 * - Debounced saves (1 second delay) to reduce API calls during typing
 * - Fallback to default template if API fails
 * - Automatic refresh when budget context changes
 *
 * @module hooks/useBudgetTemplate
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { budgetTemplateAPI, onBudgetContextChange, transactionAPI } from '../services/api';
import { useRealtime } from '../contexts/RealtimeContext';

/**
 * Hook for managing budget template state and operations.
 *
 * @param {string} month - Optional month in format "YYYY-MM" (defaults to current month)
 * @returns {Object} Template state and operations
 * @returns {Object|null} return.budgetTemplate - Template object keyed by category name
 * @returns {Function} return.setBudgetTemplate - Manually set template
 * @returns {boolean} return.showBudgetTemplate - UI visibility state
 * @returns {Function} return.setShowBudgetTemplate - Toggle template visibility
 * @returns {Function} return.getTotalTargetBudget - Sum of all target budgets
 * @returns {Function} return.getTotalPercentages - Sum of all percentages
 * @returns {Function} return.updateBudgetTemplate - Update template field (debounced save)
 * @returns {Function} return.saveBudgetTemplate - Manually save template
 * @returns {Function} return.hasValidPercentages - Check if percentages = 100%
 * @returns {Function} return.addCategoryToTemplate - Add category to template
 * @returns {Function} return.removeCategoryFromTemplate - Remove category from template
 * @returns {boolean} return.isLoading - Whether template is loading
 * @returns {boolean} return.noTemplate - Whether this month has no template
 *
 * @example
 * const template = useBudgetTemplate('2025-12');
 * console.log(template.getTotalPercentages()); // 100
 * template.updateBudgetTemplate('Groceries', 'target', 1200);
 */
export const useBudgetTemplate = (month = null, currentUserId = null) => {
    const [budgetTemplate, setBudgetTemplate] = useState(null);
    const [showBudgetTemplate, setShowBudgetTemplate] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [noTemplate, setNoTemplate] = useState(false);
    const [fillNegativeFirst, setFillNegativeFirst] = useState(false);
    const [allowOverTarget, setAllowOverTarget] = useState(false);
    const saveTimeoutRef = useRef(null);

    // Realtime context for live updates
    // getSocketId used to filter own actions by socket ID (enables same-user multi-device sync)
    const { subscribeToEvent, isConnected, getSocketId } = useRealtime();

    // Load budget template from API - but only if we have a budget context
    const loadBudgetTemplate = useCallback(async () => {
        // Wait for budget context to be set before loading
        if (!transactionAPI.getCurrentBudgetId()) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const response = await budgetTemplateAPI.get(month);

            // Check if response indicates no template
            if (response.noTemplate) {
                setNoTemplate(true);
                setBudgetTemplate({});
                setFillNegativeFirst(response.fillNegativeFirst || false);
                setAllowOverTarget(response.allowOverTarget || false);
            } else {
                setNoTemplate(false);
                // Extract allocation settings from response
                const { _allocationSettings, ...template } = response;
                setBudgetTemplate(template);
                if (_allocationSettings) {
                    setFillNegativeFirst(_allocationSettings.fillNegativeFirst || false);
                    setAllowOverTarget(_allocationSettings.allowOverTarget || false);
                }
            }
        } catch (error) {
            // Error handling - start with empty template if API fails
            // eslint-disable-next-line no-console
            console.error('Failed to load budget template:', error);
            setBudgetTemplate({});
            setNoTemplate(false);
            setFillNegativeFirst(false);
            setAllowOverTarget(false);
        } finally {
            setIsLoading(false);
        }
    }, [month]);

    useEffect(() => {
        loadBudgetTemplate();

        // Subscribe to budget context changes and reload when it's set
        const unsubscribe = onBudgetContextChange((budgetId) => {
            if (budgetId) {
                // Clear stale template immediately to prevent flash of old data
                setBudgetTemplate(null);
                setNoTemplate(false);
                loadBudgetTemplate();
            }
        });

        return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month]); // Reload when month changes

    // Subscribe to realtime template updates
    useEffect(() => {
        if (!subscribeToEvent || !isConnected) return;

        const unsubscribe = subscribeToEvent('template:updated', (data) => {
            const { template, noTemplate: isNoTemplate, month: updatedMonth, sourceSocketId } = data;

            // Skip if this event came from our own socket (we already updated optimistically)
            // Using socket ID instead of userId allows same-user multi-device sync
            const mySocketId = getSocketId();
            if (sourceSocketId && mySocketId && sourceSocketId === mySocketId) {
                return;
            }

            // Check if this update is for the month we're viewing
            // If month is null, it means current month (which is what we default to)
            // We'll reload to be safe since month comparison can be tricky
            if (updatedMonth === month || updatedMonth === null || month === null) {
                if (isNoTemplate) {
                    setNoTemplate(true);
                    setBudgetTemplate({});
                } else if (template) {
                    setNoTemplate(false);
                    setBudgetTemplate(template);
                }
            }
        });

        return unsubscribe;
    // getSocketId used for filtering own actions (same-user multi-device sync)
    }, [subscribeToEvent, isConnected, getSocketId, month]);

    const getTotalTargetBudget = () => {
        if (!budgetTemplate) return 0;
        return Object.values(budgetTemplate).reduce((sum, cat) => sum + cat.target, 0);
    };

    const getTotalPercentages = () => {
        if (!budgetTemplate) return 0;
        return Object.values(budgetTemplate).reduce((sum, cat) => sum + cat.percentage, 0);
    };

    const updateBudgetTemplate = (category, field, value) => {
        const updatedTemplate = {
            ...budgetTemplate,
            [category]: {
                ...budgetTemplate[category],
                // Store priorityGroup as integer, others as float
                [field]: field === 'priorityGroup'
                    ? parseInt(value) || 1
                    : parseFloat(value) || 0
            }
        };

        setBudgetTemplate(updatedTemplate);

        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounced save - wait 1 second after user stops typing
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await budgetTemplateAPI.update(updatedTemplate, month);
            } catch (error) {
                // Error handling
                alert('Failed to save budget template changes.');
            }
        }, 1000);
    };

    const saveBudgetTemplate = async () => {
        try {
            const updatedTemplate = await budgetTemplateAPI.update(budgetTemplate, month);

            // Check if response indicates no template
            if (updatedTemplate.noTemplate) {
                setNoTemplate(true);
                setBudgetTemplate({});
            } else {
                setNoTemplate(false);
                setBudgetTemplate(updatedTemplate);
            }
            return true;
        } catch (error) {
            // Error handling
            alert('Failed to save budget template. Please try again.');
            return false;
        }
    };

    const hasValidPercentages = () => {
        const total = getTotalPercentages();
        // Valid if 0% (no automatic allocation) or 100% (full allocation)
        // If any percentages are set (total > 0), must equal 100%
        return total === 0 || total === 100;
    };

    const addCategoryToTemplate = async (categoryName) => {
        try {
            const result = await budgetTemplateAPI.addCategory(categoryName, month);

            // Check if response indicates no template
            if (result.noTemplate) {
                setNoTemplate(true);
                setBudgetTemplate({});
            } else {
                setNoTemplate(false);
                setBudgetTemplate(result);
            }
            return true;
        } catch (error) {
            // Error handling
            throw error;
        }
    };

    const removeCategoryFromTemplate = async (categoryName) => {
        try {
            const result = await budgetTemplateAPI.removeCategory(categoryName, month);

            // Check if response indicates no template
            if (result.noTemplate) {
                setNoTemplate(true);
                setBudgetTemplate({});
            } else {
                setNoTemplate(false);
                setBudgetTemplate(result);
            }
            return true;
        } catch (error) {
            // Error handling
            throw error;
        }
    };

    return {
        budgetTemplate,
        setBudgetTemplate,
        showBudgetTemplate,
        setShowBudgetTemplate,
        getTotalTargetBudget,
        getTotalPercentages,
        updateBudgetTemplate,
        saveBudgetTemplate,
        hasValidPercentages,
        addCategoryToTemplate,
        removeCategoryFromTemplate,
        reloadBudgetTemplate: loadBudgetTemplate,
        isLoading,
        noTemplate,
        fillNegativeFirst,
        allowOverTarget,
        setFillNegativeFirst,
        setAllowOverTarget
    };
};